import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import MultiSelect from '../../../components/ui/MultiSelect'
import type { LevelOption, TaskClassification } from '../types'

interface Props {
  /** Aktif seçenekler (GET /actions/levels) — yüklenmediyse `undefined`. */
  options: { strategicObjectives?: LevelOption[]; contributions?: LevelOption[] } | undefined
  value: TaskClassification
  onChange: (next: TaskClassification) => void
  disabled?: boolean
  /**
   * `true` (varsayılan): her alan ızgaranın tüm satırını kaplar (formlar). `false`: iki alan yan yana
   * (Görev Detay'ın iki kolonlu Görev Açıklaması bölümü).
   */
  fullWidth?: boolean
}

/**
 * Görevin <b>Stratejik Hedef</b> ve <b>Katkı</b> alanları (127) — iki opsiyonel çoklu seçim. Üç yüzey bunu
 * paylaşır: Bağımsız Görev Oluştur (Dokümanlar'ın hemen üstü), toplantı bağımlı "Görev Ekle" penceresi
 * (Hedef Teslim Tarihi'nin altı) ve Görev Detay (Görev Açıklaması bölümü). Üç kopya zamanla ayrışırdı.
 *
 * <p><b>Seçenek listesi = aktif seçenekler ∪ görevde zaten seçili olanlar.</b> Sonradan pasife alınmış ya da
 * silinmiş bir kalem aktif listede yoktur ama görevde durur; seçeneklere eklenmezse MultiSelect onu
 * gösteremez ve kullanıcı kaldıramazdı. Sunucu da görevde zaten olan kalemi kabul eder, yalnız yeni
 * eklenenin Aktif olmasını ister.
 */
export default function TaskClassificationFields({
  options,
  value,
  onChange,
  disabled = false,
  fullWidth = true,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const className = fullWidth ? 'act-field act-field--full' : 'act-field'
  return (
    <>
      <ClassificationField
        className={className}
        label={t('actions.classification.strategicObjectives')}
        options={options?.strategicObjectives}
        selected={value.strategicObjectives}
        disabled={disabled}
        onChange={(strategicObjectives) => onChange({ ...value, strategicObjectives })}
      />
      <ClassificationField
        className={className}
        label={t('actions.classification.contributions')}
        options={options?.contributions}
        selected={value.contributions}
        disabled={disabled}
        onChange={(contributions) => onChange({ ...value, contributions })}
      />
    </>
  )
}

function ClassificationField({
  className,
  label,
  options,
  selected,
  disabled,
  onChange,
}: Readonly<{
  className: string
  label: string
  options: LevelOption[] | undefined
  selected: LevelOption[]
  disabled: boolean
  onChange: (next: LevelOption[]) => void
}>) {
  const { t } = useTranslation()
  const labelId = useId()
  const merged = mergeOptions(options ?? [], selected)
  return (
    <div className={className}>
      <span id={labelId} className="ui-field-label">
        {label}
      </span>
      <MultiSelect
        value={selected.map((o) => o.id)}
        options={merged}
        optionValue="id"
        optionLabel="name"
        placeholder={t('actions.classification.placeholder')}
        emptyMessage={t('actions.classification.empty')}
        aria-labelledby={labelId}
        disabled={disabled}
        onChange={(e) => {
          const ids = new Set(e.value as number[])
          onChange(merged.filter((o) => ids.has(o.id)))
        }}
      />
    </div>
  )
}

/** Aktif seçenekler (sırası korunur) + listede olmayan seçili kalemler (sona). */
function mergeOptions(active: LevelOption[], selected: LevelOption[]): LevelOption[] {
  const known = new Set(active.map((o) => o.id))
  return [...active, ...selected.filter((o) => !known.has(o.id))]
}
